import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import noop from 'lodash/noop';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import UserDatabases from '@Services/UserDatabases';
import HoverTooltip from '@Components/HoverTooltip';
import { TooltipConstants } from '@Components/Tooltip';
import Catalyst from '@Services/Catalyst';
import Similarity from '@Services/Similarity';
import NumberFormatter from '@Utils/NumberFormatter';

function ReactionTopButtonsComponent({
  metadata,
  reaction,
  index,
  toggleHitDetailsOpen,
  isHitDetailsOpen,
  isAllDetailsOpen,
  toggleAllDetailsOpen,
}) {
  const findSimilarReactions = () => {
    const databaseId = reaction.databaseId || metadata.databaseId;
    const { hitSetKey } = metadata;
    Similarity.showModalForReaction(reaction.id, databaseId, hitSetKey, reaction.rxnFile);
  };

  const onReactionLinkClick = (link) => {
    const dbName = UserDatabases.getDbName(get(metadata, 'databaseId'));
    const linkName = link.label;
    const linkType = `menu:${dbName}`;
    const actionName = `rx2:openindatabase:${link.label}`;

    Catalyst.trackContentLinkClick(linkName, linkType, actionName);

    ReactReactionDetailsHelper.goToContext('reactions', link, metadata);
  };

  const getReactionLinks = () => (
    <ul>
      {!isEmpty(reaction.reactionLinks)
        ? reaction.reactionLinks.map((link) => (
            <ButtonWithIcon
              key={link.databaseId}
              sprite="ChevronRight"
              type="link"
              linkIconSize="xs"
              className={classnames('e2e-drop-item', `e2e-${kebabCase(link.label)}`)}
              onClick={() => onReactionLinkClick(link)}>
              {link.label}
            </ButtonWithIcon>
          ))
        : null}
    </ul>
  );

  const btnClasses = classnames('els-btn', 'els-btn-sm', 'els-btn-link');
  const isSimilarityBtnDisabled = !get(metadata, 'isSimilaritySearchAvailable');
  const similarityBtnDisabledTooltip = isSimilarityBtnDisabled ? 'No similar reactions available' : '';
  const tooltipId = TooltipConstants.ids.GlobalHover;

  return (
    <div className="rx-reaction-buttons">
      {reaction.hitDetailsCount > 0 && (
        <button
          type="button"
          className={`${btnClasses}${isHitDetailsOpen ? ' active' : ''} e2e-reaction-hits`}
          onClick={toggleHitDetailsOpen}
          aria-expanded={isHitDetailsOpen}
          aria-controls={`reaction-hit-details-${index}`}
          data-click-delegation
          data-tracking={!isHitDetailsOpen}
          data-tracking-type="contentlinkclick"
          data-tracking-action={reaction.hitDetailsCount !== reaction.detailsCount ? 'Hits' : 'Hits/Conditions'}>
          <span className="rx-count-badge rx-count-badge--small no-border">{NumberFormatter(reaction.hitDetailsCount)}</span>
          <span>{reaction.hitDetailsCount !== reaction.detailsCount ? 'Hits' : 'Hits/Conditions'}</span>
          <svg className="els-btn-icon">
            <use xlinkHref={`${isHitDetailsOpen ? '#icon-chevron-up' : '#icon-chevron-down'}`} />
          </svg>
        </button>
      )}
      {reaction.detailsCount > 0 && reaction.hitDetailsCount !== reaction.detailsCount && (
        <button
          type="button"
          className={`${btnClasses}${isAllDetailsOpen ? ' active' : ''} e2e-reaction-conditions`}
          onClick={toggleAllDetailsOpen}
          aria-expanded={isAllDetailsOpen}
          aria-controls={`reaction-details-${index}`}
          data-click-delegation
          data-tracking={!isAllDetailsOpen}
          data-tracking-type="contentlinkclick"
          data-tracking-action="Conditions">
          <span className="rx-count-badge rx-count-badge--small no-border">{NumberFormatter(reaction.detailsCount)}</span>
          <span>Conditions</span>
          <svg className="els-btn-icon">
            <use xlinkHref={`${isAllDetailsOpen ? '#icon-chevron-up' : '#icon-chevron-down'}`} />
          </svg>
        </button>
      )}
      <HoverTooltip id={tooltipId} tooltip={similarityBtnDisabledTooltip} className="shopping-cart-tooltip" theme="simple">
        <button
          type="button"
          className={`${btnClasses} e2e-reaction-find-similar`}
          disabled={isSimilarityBtnDisabled}
          onClick={findSimilarReactions}>
          <span>Find Similar</span>
          <svg className="els-btn-icon">
            <use xlinkHref="#icon-chevron-right" />
          </svg>
        </button>
      </HoverTooltip>
      <span className="rx-reaction-id">Reaction ID: {reaction.id}</span>
      {reaction.reactionLinks && (
        <Flyout
          className="rx-reaction-open-in-database"
          flyout={
            <div className="u-els-font-size-base u-els-color-text u-els-margin-right-2x">
              <div className="u-els-margin-bottom u-els-font-size-h4 u-els-nowrap">Open in database</div>
              {getReactionLinks()}
            </div>
          }
          shouldCloseOnESC
          showCloseButton
          placement="bottom-end"
          pointerType="primary">
          <ButtonWithIcon
            sprite="ChevronDown"
            type="link"
            linkIconSize="xs"
            iconRight
            onClick={noop}
            className="e2e-reaction-open-in-database">
            Open in database
          </ButtonWithIcon>
        </Flyout>
      )}
    </div>
  );
}

ReactionTopButtonsComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  reaction: PropTypes.object.isRequired,
  index: PropTypes.string,
  isHitDetailsOpen: PropTypes.bool,
  isAllDetailsOpen: PropTypes.bool,
  toggleHitDetailsOpen: PropTypes.func.isRequired,
  toggleAllDetailsOpen: PropTypes.func.isRequired,
};

export default ReactionTopButtonsComponent;
